import { AsyncPipe } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, first, map, takeUntil } from 'rxjs/operators';
import { FacettenComponent } from '../../components';
import { BildungsbereichAendernCardComponent } from '../../components/bildungsbereich-aendern-card/bildungsbereich-aendern-card.component';
import { FeedbackComponent } from '../../components/feedback/feedback.component';
import { ResponsiveContextInfoHeaderComponent } from '../../components/responsive-context-info-header/responsive-context-info-header.component';
import { SuchergebnisHitsCardComponent } from '../../components/suchergebnis/suchergebnis-hits-card/suchergebnis-hits-card.component';
import { SuchergebnisComponent } from '../../components/suchergebnis/suchergebnis.component';
import { SuchleisteComponent } from '../../components/suchleiste/suchleiste.component';
import { LIB_CONFIG, LibConfig } from '../../lib.config';
import { ScreenSizeService, ViewMode } from '../../services/screen-size';
import { CommonStateService } from '../../services/state/common-state.service';
import { LastAngebotService } from '../../services/suchseite/last-angebot.service';
import { OrtService } from '../../services/suchseite/ort.service';
import { PaginationService } from '../../services/suchseite/pagination.service';
import { SuchwortService } from '../../services/suchseite/suchwort.service';
import { ScrollToAndFocusDirective } from '../../shared/directives';
import { CleanupService } from '../../shared/utils/cleanup.service';

@Component({
  selector: 'now-wbsuche-lib-suche',
  templateUrl: './suche.component.html',
  styleUrls: ['./suche.component.scss'],
  providers: [CleanupService],
  imports: [
    ResponsiveContextInfoHeaderComponent,
    ScrollToAndFocusDirective,
    SuchleisteComponent,
    SuchergebnisHitsCardComponent,
    FacettenComponent,
    SuchergebnisComponent,
    BildungsbereichAendernCardComponent,
    FeedbackComponent,
    AsyncPipe
  ]
})
export class SucheComponent implements AfterViewInit {
  private readonly lastAngebotService = inject(LastAngebotService);
  libConfig = inject<LibConfig>(LIB_CONFIG);
  private readonly screenSizeService = inject(ScreenSizeService);
  private readonly cleanupService = inject(CleanupService);
  private readonly suchwortService = inject(SuchwortService);
  private readonly paginationService = inject(PaginationService);
  private readonly commonStateService = inject(CommonStateService);
  private readonly ortService = inject(OrtService);

  private readonly isInView = false;

  @Input()
  postleitzahl?: string;

  @Input()
  bildungsziele: number[] = [];

  @Output()
  zeigeDetailseite = new EventEmitter<string>();

  public showRequiredInputState$ = this.suchwortService.showRequiredInputState$;
  public pageInfos$ = this.paginationService.pageInfos$;
  public readonly textSucheInputElementId = 'textSuche';

  /**
   *  Bei Änderung des selektierten Angebots,
   *  filtere, dass kein Angebot gesetzt ist,
   *  filtere nur auf ersten Zustandwechsel, weitere werden inoriert, also nur bei Seitenladen
   */
  public scrollToHeader$ = this.commonStateService.selectedAngebotId$.pipe(
    map((id) => !id),
    filter(Boolean),
    first()
  );

  public showBildungsbereichAendernCard$ = combineLatest([
    this.ortService.ort$,
    this.suchwortService.suchwort$
  ]).pipe(
    map(([ort, suchwort]) => {
      return !!ort || suchwort.length > 0;
    })
  );

  public mobile$ = this.screenSizeService.rangeTo$(ViewMode.TABLET);
  public isDesktop$ = this.screenSizeService.rangeFrom$(ViewMode.DESKTOP);

  ngAfterViewInit() {
    this.lastAngebotService.angebotId$
      .pipe(
        takeUntil(this.cleanupService.destroyed$),
        filter((angebot) => !!angebot),
        distinctUntilChanged()
      )
      .subscribe({
        next: (angebot) => {
          if (!this.isInView && angebot) {
            this.entferneAngebotAusUrl();
          }
        }
      });
  }

  public triggerSearch(): void {
    this.suchwortService.triggerSearch();
  }

  private entferneAngebotAusUrl() {
    this.lastAngebotService.setzeAngebot(null);
  }
}
